/** One field of a collection row as a compact table cell (the tour plan §2 S1: the
 *  identifiers and tag-categories tables) — FieldSlot's state chrome folded into a table
 *  cell, where a column header is the label and the row carries the captions.
 *
 *  - View mode shows the STORED value; edit mode swaps in a compact control in the same
 *    cell (the table's column widths are fixed, so nothing moves on Edit).
 *  - Changed: the Pending Amber tint, and a revert button whose name says what it was
 *    ("Revert Matching of GYG-1 — was parked"). Stale and invalid: an icon in the cell;
 *    the words (Keep mine / Take theirs, the message) are on the row's issues line
 *    (`RowIssues`), which the table renders under the row.
 *  - A row the draft creates shows its defaults (`config.defaultValueOf`). `required`: a
 *    new row's empty value is an invalid input — Save is blocked and the cell says why,
 *    before the server could refuse it.
 *  - `changesFor` turns one edit into several paths (a tag-category flag that sets or
 *    clears its dependants) — one undo step.
 *  - A ledger row focuses the cell (the editor's focus registry). */
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import SyncProblemIcon from '@mui/icons-material/SyncProblem';
import { Box, Button, Checkbox, IconButton, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
import { useEffect, useRef, type ReactNode } from 'react';

import { BoolText } from '@/components/BoolText';
import { parseRecordKey, type RecordKey } from '@/features/records/draft/changeSet';
import { useDraft, useDraftField } from '@/features/records/draft/DraftProvider';
import { EMPTY_DISPLAY, isEmptyValue } from '@/features/records/editor/format';
import { useRecordEditor } from '@/features/records/editor/RecordEditorProvider';
import type { RecordField } from '@/features/records/editor/types';
import { CHANGED_TINT } from '@/features/records/fields/FieldSlot';

// Moved to the shared components (the tag-category associations table uses it too).
export { BoolText };

/** Room the edit-mode revert button takes at the cell's end — in both modes. */
const REVERT_PX = 22;

let cellSession = 0;

export interface CellChange {
  rec: RecordKey;
  path: string;
  to: unknown;
}

export interface DraftCellProps {
  rec: RecordKey;
  path: string;
  /** The column's label ("Matching"). */
  label: string;
  /** The row's name ("GYG-1") — completes the control's accessible name. */
  rowName: string;
  /** Monospace value (an ID). */
  mono?: boolean;
  /** The label beside a checkbox (a details row of flags). */
  showLabel?: boolean;
  /** The stored value's rendering in view mode (and for a read-only cell). */
  display?: (value: unknown) => ReactNode;
  /** One edit → several paths (one undo step). Default: the cell's own path. */
  changesFor?: (to: unknown) => CellChange[];
  /** A new row's empty value blocks Save (an invalid input). */
  required?: boolean;
  /** Why the value cannot be saved as it stands (a duplicate) — an invalid input that
   *  blocks Save, shown on the cell. */
  invalidReason?: string | null;
  /** Why the control is disabled although the field is editable (a limit). */
  disabledReason?: string | null;
}

/** The value a path of a row shows: the draft's in edit mode, the stored one (a new
 *  row's default) otherwise. */
export function useCellValue(rec: RecordKey, path: string): {
  value: unknown;
  baseline: unknown;
  changed: boolean;
  editing: boolean;
} {
  const { mode, config } = useRecordEditor();
  const { entity, id } = parseRecordKey(rec);
  const draft = useDraftField(entity, id, path);
  const editing = mode === 'edit';
  const baseline = draft.baseline === undefined ? config.defaultValueOf?.(rec, path) : draft.baseline;
  return {
    value: editing && draft.entry ? draft.entry.to : baseline,
    baseline,
    changed: editing && draft.changed,
    editing,
  };
}

export function DraftCell({
  rec,
  path,
  label,
  rowName,
  mono = false,
  showLabel = false,
  display,
  changesFor,
  required = false,
  invalidReason = null,
  disabledReason = null,
}: DraftCellProps) {
  const { mode, config, fieldOf, formatValue, validateValue, registerFocus, errorsFor, setInvalidInput } = useRecordEditor();
  const { state, dispatch, baselineOf } = useDraft();
  const { entity, id } = parseRecordKey(rec);
  const draft = useDraftField(entity, id, path);
  const field = fieldOf(entity, path);
  const editing = mode === 'edit';
  const editability = field ? (config.editabilityOf?.(rec, field, state) ?? { editable: true }) : { editable: false };
  const canEdit = editing && Boolean(field) && field!.kind === 'editable' && field!.widget !== null && editability.editable;
  const baseline = draft.baseline === undefined ? config.defaultValueOf?.(rec, path) : draft.baseline;
  const value = editing && draft.entry ? draft.entry.to : baseline;
  const changed = editing && draft.changed;
  const stale = editing ? draft.stale : undefined;
  const name = `${label} of ${rowName}`;
  const controlRef = useRef<HTMLElement | null>(null);
  const cellRef = useRef<HTMLDivElement>(null);

  // A new row's required value: empty blocks Save, with the reason on the cell.
  const missing = required && id < 0 && editing && isEmptyValue(typeof value === 'string' ? value.trim() : value);
  const invalid = missing ? `${label} is required` : editing ? invalidReason : null;
  useEffect(() => {
    setInvalidInput(rec, path, invalid);
  }, [invalid, rec, path, setInvalidInput]);
  useEffect(() => () => setInvalidInput(rec, path, null), [rec, path, setInvalidInput]);

  useEffect(
    () =>
      registerFocus(rec, path, () => {
        cellRef.current?.scrollIntoView?.({ block: 'center' });
        (controlRef.current ?? cellRef.current)?.focus({ preventScroll: true });
      }),
    [registerFocus, rec, path],
  );

  const validation = changed && field && draft.entry ? validateValue(field, draft.entry.to) : null;
  const errors = editing ? [...(validation ? [validation] : []), ...errorsFor(rec, path)] : [];
  const wasText = field ? formatValue(field, rec, draft.entry ? draft.entry.from : baseline) : '';

  /** The value a change is made from: the stored one, or a new row's default (an edit
   *  back to the default is then no change). */
  const fromOf = (r: RecordKey, p: string) => {
    const b = baselineOf(r, p);
    return b === undefined ? config.defaultValueOf?.(r, p) : b;
  };
  // A text input's keystrokes in one focus session undo as one step.
  const token = useRef<string | null>(null);
  const begin = () => {
    cellSession += 1;
    token.current = `${rec}|${path}#${cellSession}`;
  };
  const end = () => {
    token.current = null;
  };
  const set = (to: unknown) => {
    const list = changesFor ? changesFor(to) : [{ rec, path, to }];
    dispatch({
      type: 'SET',
      changes: list.map((c) => ({ rec: c.rec, path: c.path, from: fromOf(c.rec, c.path), to: c.to })),
      ...(token.current ? { coalesce: token.current } : {}),
    });
  };
  const revert = () =>
    dispatch({ type: 'REVERT', targets: [{ rec, path }, ...(config.cascadeTargets?.(rec, path, state) ?? [])] });

  let content: ReactNode;
  if (canEdit && field) {
    content = (
      <CellControl
        field={field}
        value={value}
        name={name}
        label={label}
        showLabel={showLabel}
        mono={mono}
        error={errors.length > 0}
        disabled={Boolean(disabledReason)}
        disabledReason={disabledReason}
        onChange={set}
        onFocus={begin}
        onBlur={end}
        inputRef={(el) => {
          controlRef.current = el;
        }}
      />
    );
  } else {
    content = (
      <ReadCell
        field={field}
        value={value}
        label={label}
        showLabel={showLabel}
        mono={mono}
        display={display}
        tooltip={editing && !editability.editable ? (editability.reason ?? null) : null}
        text={field ? formatValue(field, rec, value) : String(value ?? EMPTY_DISPLAY)}
      />
    );
  }

  return (
    <Box
      ref={cellRef}
      tabIndex={-1}
      data-cell={path}
      data-rec={rec}
      data-state={[changed && 'changed', stale && 'stale', errors.length > 0 && 'invalid'].filter(Boolean).join(' ') || undefined}
      sx={[
        {
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
          minHeight: 32,
          minWidth: 0,
          borderRadius: 1,
          outline: 'none',
          px: 0.25,
          transition: 'background-color 150ms ease-out',
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          '&:focus-visible': { boxShadow: (t) => `0 0 0 2px ${t.palette.primary.main}` },
        },
        changed && { bgcolor: CHANGED_TINT },
      ]}
    >
      <Box sx={{ flex: '1 1 auto', minWidth: 0, display: 'flex', alignItems: 'center' }}>{content}</Box>
      {stale && (
        <Tooltip title="Changed on the server — Keep mine or Take theirs under the row">
          <SyncProblemIcon aria-label={`${name} changed on the server`} sx={{ fontSize: 16, color: 'warning.main', flex: 'none' }} />
        </Tooltip>
      )}
      {errors.length > 0 && (
        <Tooltip title={errors.join(' · ')}>
          <ErrorOutlineIcon aria-label={`${name}: ${errors.join(' · ')}`} sx={{ fontSize: 16, color: 'error.main', flex: 'none' }} />
        </Tooltip>
      )}
      {/* Reserved in both modes: Edit moves nothing (Zero-Re-Layout). */}
      <Box sx={{ width: REVERT_PX, flex: 'none', display: 'flex', justifyContent: 'center' }}>
        {changed && !stale && (
          <Tooltip title={`Revert — was ${wasText}`}>
            <IconButton size="small" aria-label={`Revert ${name} — was ${wasText}`} onClick={revert} sx={{ p: 0.25 }}>
              <ReplayIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}

function ReadCell({
  field,
  value,
  label,
  showLabel,
  mono,
  display,
  tooltip,
  text,
}: {
  field: RecordField | undefined;
  value: unknown;
  label: string;
  showLabel: boolean;
  mono: boolean;
  display: ((value: unknown) => ReactNode) | undefined;
  tooltip: string | null;
  text: string;
}) {
  let node: ReactNode;
  if (display) node = display(value);
  else if (field?.widget === 'boolean') {
    node = showLabel ? (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
        <BoolText on={value === true} onText="" offText="" />
        <Typography component="span" variant="body2" color={value === true ? 'text.primary' : 'text.secondary'}>
          {label}
          <Box component="span" sx={visuallyHidden}>
            {value === true ? ': on' : ': off'}
          </Box>
        </Typography>
      </Box>
    ) : (
      <BoolText on={value === true} onText={text} offText={text} />
    );
  } else {
    const empty = isEmptyValue(value);
    node = (
      <Typography
        component="span"
        variant="body2"
        sx={{
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          color: empty ? 'text.disabled' : 'text.primary',
          ...(mono ? { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: '0.8125rem' } : {}),
        }}
      >
        {empty ? EMPTY_DISPLAY : text}
      </Typography>
    );
  }
  return tooltip ? (
    <Tooltip title={tooltip} placement="bottom-start">
      <Box component="span" sx={{ minWidth: 0, display: 'inline-flex' }}>
        {node}
      </Box>
    </Tooltip>
  ) : (
    <>{node}</>
  );
}

function CellControl({
  field,
  value,
  name,
  label,
  showLabel,
  mono,
  error,
  disabled,
  disabledReason,
  onChange,
  onFocus,
  onBlur,
  inputRef,
}: {
  field: RecordField;
  value: unknown;
  name: string;
  label: string;
  showLabel: boolean;
  mono: boolean;
  error: boolean;
  disabled: boolean;
  disabledReason: string | null;
  onChange: (to: unknown) => void;
  onFocus: () => void;
  onBlur: () => void;
  inputRef: (el: HTMLElement | null) => void;
}) {
  const inputSx = {
    '& .MuiInputBase-root': { fontSize: '0.875rem' },
    '& .MuiInputBase-input': {
      py: 0.5,
      px: 1,
      ...(mono ? { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: '0.8125rem' } : {}),
    },
  };
  if (field.widget === 'boolean') {
    const box = (
      <Checkbox
        size="small"
        checked={value === true}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        inputProps={{ 'aria-label': name }}
        inputRef={inputRef}
        sx={{ p: 0.5 }}
      />
    );
    const control = showLabel ? (
      <Box component="label" sx={{ display: 'inline-flex', alignItems: 'center', minWidth: 0, cursor: disabled ? 'default' : 'pointer' }}>
        {box}
        <Typography component="span" variant="body2" color={disabled ? 'text.disabled' : 'text.primary'}>
          {label}
        </Typography>
      </Box>
    ) : (
      box
    );
    return disabledReason ? (
      <Tooltip title={disabledReason}>
        <span>{control}</span>
      </Tooltip>
    ) : (
      control
    );
  }
  if (field.widget === 'enum') {
    const known = field.choices.some((c) => c.value === value);
    return (
      <TextField
        select
        size="small"
        fullWidth
        value={known ? value : ''}
        error={error}
        disabled={disabled}
        onChange={(e) => {
          const choice = field.choices.find((c) => String(c.value) === String(e.target.value));
          onChange(choice ? choice.value : null);
        }}
        inputRef={inputRef}
        SelectProps={{ inputProps: { 'aria-label': name } }}
        sx={inputSx}
      >
        {field.choices.map((c) => (
          <MenuItem key={String(c.value)} value={c.value}>
            {c.label}
          </MenuItem>
        ))}
      </TextField>
    );
  }
  if (field.widget === 'integer') {
    return (
      <TextField
        size="small"
        fullWidth
        type="number"
        value={typeof value === 'number' ? value : ''}
        error={error}
        disabled={disabled}
        onChange={(e) => {
          const n = e.target.value === '' ? 0 : Number(e.target.value);
          if (Number.isInteger(n) && n >= 0) onChange(n);
        }}
        onFocus={onFocus}
        onBlur={onBlur}
        inputRef={inputRef}
        inputProps={{ 'aria-label': name, min: 0, step: 1 }}
        sx={inputSx}
      />
    );
  }
  return (
    <TextField
      size="small"
      fullWidth
      value={typeof value === 'string' ? value : value == null ? '' : String(value)}
      error={error}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      onFocus={onFocus}
      onBlur={onBlur}
      inputRef={inputRef}
      inputProps={{
        'aria-label': name,
        autoComplete: 'off',
        spellCheck: false,
        ...(field.max_length ? { maxLength: field.max_length } : {}),
      }}
      sx={inputSx}
    />
  );
}

export interface RowIssue {
  rec: RecordKey;
  path: string;
  label: string;
}

/** The words of a row's cell states (edit mode): server errors and stale values with
 *  Keep mine / Take theirs. Renders nothing while the row is clean. */
export function RowIssues({ cells }: { cells: RowIssue[] }) {
  const { mode, errorsFor, formatValue, fieldOf } = useRecordEditor();
  const { state, dispatch } = useDraft();
  if (mode !== 'edit') return null;
  const lines: ReactNode[] = [];
  for (const c of cells) {
    const entry = state.entries[c.rec]?.[c.path];
    const field = fieldOf(parseRecordKey(c.rec).entity, c.path);
    if (entry?.stale?.gone) {
      lines.push(
        <Box key={`gone:${c.rec}:${c.path}`} data-row-issue="gone" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <SyncProblemIcon aria-hidden sx={{ fontSize: 16, color: 'warning.main' }} />
          <Typography variant="caption">{c.label}: deleted elsewhere — this edit can’t be saved</Typography>
          <Button size="small" variant="outlined" sx={{ py: 0, minHeight: 24 }} onClick={() => dispatch({ type: 'TAKE_THEIRS', target: { rec: c.rec, path: c.path } })}>
            Drop this edit
          </Button>
        </Box>,
      );
    } else if (entry?.stale) {
      const now = field ? formatValue(field, c.rec, entry.stale.current) : String(entry.stale.current ?? EMPTY_DISPLAY);
      lines.push(
        <Box key={`stale:${c.rec}:${c.path}`} data-row-issue="stale" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <SyncProblemIcon aria-hidden sx={{ fontSize: 16, color: 'warning.main' }} />
          <Typography variant="caption">
            {c.label}: changed on the server to <b>{now}</b>
            {entry.stale.changedBy?.label ? ` by ${entry.stale.changedBy.label}` : ''}
          </Typography>
          <Button size="small" variant="outlined" sx={{ py: 0, minHeight: 24 }} onClick={() => dispatch({ type: 'KEEP_MINE', target: { rec: c.rec, path: c.path } })}>
            Keep mine
          </Button>
          <Button size="small" sx={{ py: 0, minHeight: 24 }} onClick={() => dispatch({ type: 'TAKE_THEIRS', target: { rec: c.rec, path: c.path } })}>
            Take theirs
          </Button>
        </Box>,
      );
    }
    for (const e of errorsFor(c.rec, c.path)) {
      lines.push(
        <Typography key={`err:${c.rec}:${c.path}:${e}`} data-row-issue="invalid" variant="caption" color="error" sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
          <ErrorOutlineIcon aria-hidden sx={{ fontSize: 16 }} />
          <span>
            {c.label}: {e}
          </span>
        </Typography>,
      );
    }
  }
  if (lines.length === 0) return null;
  return <Box sx={{ display: 'grid', gap: 0.25, py: 0.5 }}>{lines}</Box>;
}

// px strings: in sx a bare `1` is 100%.
const visuallyHidden = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  m: '-1px',
  p: 0,
  border: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;
